import ExportToExcelButton from "@app/components/export-to-excel-button";
import type { XlsxCell, XlsxColumnType } from "@app/lib/export-xlsx";
import { Fragment, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { diligenceMatrixTables_Query } from "@app/queries/diligenceMatrixTables_Query.graphql";
import type { DiligenceKpiCellScope } from "./diligence-kpi-cell-panel";

const matrixQuery = graphql`
  query diligenceMatrixTables_Query($dimension: String, $band: String) {
    diligenceMatrix(dimension: $dimension, band: $band) {
      available
      generatedAt
      months
      notes
      segments {
        dimension
        band
        label
      }
      rows {
        key
        label
        group
        format
        dimension
        band
        drillable
        ttm
        mom
      }
    }
  }
`;

type Basis = "ttm" | "mom";

// How much of the axis to draw. The full axis is every month of company history,
// which is a spreadsheet; a screen wants a window.
const WINDOWS = [
  { key: "12", label: "12 months", months: 12 },
  { key: "24", label: "24 months", months: 24 },
  { key: "36", label: "36 months", months: 36 },
  { key: "all", label: "All history", months: Number.MAX_SAFE_INTEGER },
] as const;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const monthName = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });

const fmt = (value: number | null | undefined, format: string) => {
  if (value == null) return "—";
  switch (format) {
    case "money":
      return usd.format(value);
    case "percent":
      return `${(value * 100).toFixed(1)}%`;
    case "int":
      return value.toLocaleString();
    default:
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
};

const xlsxType = (format: string): XlsxColumnType =>
  format === "money" || format === "percent" || format === "int"
    ? format
    : "number";

// The picker's value carries both halves of a segment, joined on a separator
// that cannot appear in a band name. Every tenure band has a space in it
// ("13-24 mo"), so splitting a space-joined key hands back a truncated band
// the resolver cannot find - and the table falls back to Overall while the
// picker claims otherwise.
const SEPARATOR = "|";
const segmentKey = (dimension: string, band: string) =>
  `${dimension}${SEPARATOR}${band}`;

export default function DiligenceMatrixTables({
  refreshKey,
  onCell,
}: {
  // Bumped by the page when a report finishes. A fetchKey bump alone does
  // nothing under store-or-network, so the policy has to change with it or the
  // tables would keep drawing the previous run's matrix.
  refreshKey: number;
  onCell: (scope: DiligenceKpiCellScope) => void;
}) {
  const [segment, setSegment] = useState(segmentKey("overall", "Overall"));
  const [dimension, band] = segment.split(SEPARATOR);
  const [basis, setBasis] = useState<Basis>("ttm");
  const [windowKey, setWindowKey] = useState<string>("24");
  const [copied, setCopied] = useState(false);

  const data = useLazyLoadQuery<diligenceMatrixTables_Query>(
    matrixQuery,
    { dimension, band },
    {
      fetchKey: refreshKey,
      fetchPolicy: refreshKey > 0 ? "network-only" : "store-or-network",
    }
  );

  const matrix = data.diligenceMatrix;
  const windowSize =
    WINDOWS.find((entry) => entry.key === windowKey)?.months ?? 24;
  const months = matrix.months;
  const rows = matrix.rows;

  // The visible slice of the axis, carrying its index into the value arrays —
  // those are month-aligned to the WHOLE axis, not to what is on screen.
  const columns = useMemo(() => {
    const from = Math.max(0, months.length - windowSize);
    return months
      .slice(from)
      .map((month, offset) => ({ month, index: from + offset }));
  }, [months, windowSize]);

  // Metric-major within a section, which is how the workbook reads.
  const groups = useMemo(() => {
    const ordered: { group: string; rows: typeof rows }[] = [];
    rows.forEach((row) => {
      const last = ordered[ordered.length - 1];
      if (last && last.group === row.group) last.rows = [...last.rows, row];
      else ordered.push({ group: row.group, rows: [row] });
    });
    return ordered;
  }, [rows]);

  const segmentLabel =
    matrix.segments.find(
      (option) => option.dimension === dimension && option.band === band
    )?.label ?? "Overall (whole book)";

  const build = () => ({
    filename: `diligence-kpis-${band}-${basis}`.replace(/[^a-zA-Z0-9-]+/g, "-"),
    sheetName: basis === "ttm" ? "TTM" : "Month over month",
    columns: [
      { label: "Metric", type: "text" as XlsxColumnType },
      ...columns.map((column) => ({
        label: monthName(column.month),
        type: "number" as XlsxColumnType,
      })),
    ],
    rows: rows.map(
      (row) =>
        [
          `${row.group} — ${row.label}`,
          ...columns.map((column) => ({
            // "" and never null: Rails drops nil array entries, shifting the row.
            v: row[basis][column.index] ?? "",
            t: xlsxType(row.format),
          })),
        ] as XlsxCell[]
    ),
    notes: [
      `${segmentLabel} · ${
        basis === "ttm" ? "trailing twelve months" : "month over month"
      }.`,
      ...matrix.notes,
    ],
  });

  // What a reader gets by selecting the table by hand, without the fight with
  // the browser: the visible segment, basis and month window, formatted as they
  // are on screen and tab-separated — so it pastes as cells into a spreadsheet
  // and as a table into a model. The leading line is what makes the numbers
  // readable once they are out of the page: a column of ratios means nothing
  // without knowing which band and which basis they are.
  const copyTable = async () => {
    const lines = [
      `${segmentLabel} · ${
        basis === "ttm" ? "trailing twelve months" : "month over month"
      }`,
      ["Metric", ...columns.map((column) => monthName(column.month))].join(
        "\t"
      ),
      ...rows.map((row) =>
        [
          `${row.group} — ${row.label}`,
          ...columns.map((column) => {
            const value = row[basis][column.index];
            return value == null ? "" : fmt(value, row.format);
          }),
        ].join("\t")
      ),
    ];

    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.alert("Could not copy — your browser blocked clipboard access.");
    }
  };

  if (!matrix.available) {
    return (
      <div className="rounded border border-dashed border-gray-300 p-6 text-sm text-gray-500">
        The tables appear here once a report has been generated. They are the
        numbers from the workbook itself rather than a fresh calculation, so
        what is on screen and what is in the file you download are the same to
        the cent.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={segment}
          onChange={(event) => setSegment(event.target.value)}
          className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-700"
          aria-label="Segment"
        >
          {matrix.segments.map((option) => (
            <option
              key={segmentKey(option.dimension, option.band)}
              value={segmentKey(option.dimension, option.band)}
            >
              {option.label}
            </option>
          ))}
        </select>

        <div className="inline-flex overflow-hidden rounded border border-gray-200">
          {(["ttm", "mom"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setBasis(option)}
              className={`px-2 py-1 text-sm ${
                basis === option
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {option === "ttm" ? "Trailing 12 months" : "Month over month"}
            </button>
          ))}
        </div>

        <div className="inline-flex overflow-hidden rounded border border-gray-200">
          {WINDOWS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setWindowKey(option.key)}
              className={`px-2 py-1 text-sm ${
                windowKey === option.key
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <ExportToExcelButton build={build} label="Export this view" />

        <button
          type="button"
          onClick={copyTable}
          title="Copy this table to the clipboard, tab-separated — paste it into a spreadsheet or a model"
          className="whitespace-nowrap rounded border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
        >
          {copied ? "Copied" : "Copy table"}
        </button>

        {matrix.generatedAt && (
          <span className="ml-auto text-xs text-gray-400">
            Generated {new Date(matrix.generatedAt).toLocaleString()}
          </span>
        )}
      </div>

      <div className="overflow-x-auto border border-gray-200">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-20 min-w-[16rem] border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Metric
              </th>
              {columns.map((column) => (
                <th
                  key={column.month}
                  className="whitespace-nowrap border-b border-gray-200 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  {monthName(column.month)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.group}>
                <tr>
                  <th
                    colSpan={columns.length + 1}
                    className="sticky left-0 border-y border-gray-200 bg-gray-50 px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {group.group}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.key} className="hover:bg-blue-50">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 border-r border-gray-200 bg-white px-3 py-1.5 text-left font-normal text-gray-700"
                    >
                      {row.label}
                    </th>
                    {columns.map((column) => {
                      const value = row[basis][column.index];
                      const drillable = row.drillable && value != null;
                      return (
                        <td
                          key={column.month}
                          className={`whitespace-nowrap px-3 py-1.5 text-right font-mono tabular-nums ${
                            (value ?? 0) < 0 ? "text-red-600" : "text-gray-800"
                          }`}
                        >
                          {drillable ? (
                            <button
                              type="button"
                              onClick={() => {
                                // A drag that begins and ends inside one cell
                                // still fires that cell's click, so highlighting
                                // a number to copy it would open the panel
                                // instead. A click on a page that already had a
                                // selection collapses it on mousedown, so a
                                // selection surviving to here means this gesture
                                // was a drag: the reader was selecting, not
                                // asking what the number is made of.
                                if (window.getSelection()?.toString()) return;
                                onCell({
                                  metric: row.key,
                                  label: row.label,
                                  month: column.month,
                                  basis,
                                  dimension: row.dimension,
                                  band: row.band,
                                  segmentLabel,
                                  cellValue: value,
                                });
                              }}
                              title={`Show what ${row.label} in ${monthName(
                                column.month
                              )} is made of`}
                              // select-text overrides the user-agent
                              // `user-select: none` that browsers put on every
                              // button. Without it a drag across the table stops
                              // dead at the first drillable number, so the table
                              // cannot be copied out — and this one exists to be
                              // pasted into a spreadsheet or a model.
                              className="select-text rounded px-1 underline decoration-dotted decoration-gray-300 underline-offset-4 hover:bg-blue-100 hover:text-blue-700 hover:decoration-blue-400"
                            >
                              {fmt(value, row.format)}
                            </button>
                          ) : (
                            fmt(value, row.format)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="max-w-4xl list-disc space-y-1 pl-5 text-xs text-gray-500">
        {matrix.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </div>
  );
}
